/**
 * The `?start=` brief: what the "Design your kitchen" page hands the kitchen
 * planner so it opens on a design already started (plan K1/K2).
 *
 * <p>This module is the contract. The planner side (K2, `Bootstrap` reading
 * `?start=`) is not built yet; when it is, it decodes with
 * {@link decodeStartBrief} from here rather than its own parser, so the two
 * cannot drift.
 *
 * <p>Wire format: `?start=` + URI-encoded JSON, versioned by `v`. Readable in
 * the address bar and in logs, and short enough with 500 characters of wishes.
 * Decoding is strict: anything unknown or out of range is dropped rather than
 * trusted, since the address is shopper-editable.
 */
export type RoomShape = 'single' | 'galley' | 'l' | 'u' | 'island';

export interface StartBrief {
  v: 1;
  room: 'kitchen';
  shape: RoomShape;
  /** Wall lengths in mm, one per wall of the shape (A, B, C). */
  walls: number[];
  /** Ceiling height in mm. */
  height: number;
  /** Door range and colour ids from the door catalogue. */
  range?: string;
  colour?: string;
  /** Appliances and must-haves, from {@link MUST_HAVES}. */
  must: string[];
  /** Free-text wishes; when present the planner runs its AI wizard straight away. */
  wishes?: string;
}

export const SHAPES: Record<RoomShape, { name: string; walls: string[]; lengths: number[] }> = {
  single: { name: 'Single wall', walls: ['A'], lengths: [3600] },
  galley: { name: 'Galley', walls: ['A', 'B'], lengths: [3200, 3200] },
  l: { name: 'L-shaped', walls: ['A', 'B'], lengths: [3600, 2800] },
  u: { name: 'U-shaped', walls: ['A', 'B', 'C'], lengths: [2600, 3400, 2600] },
  island: { name: 'L with island', walls: ['A', 'B'], lengths: [4000, 3200] },
};

export const MUST_HAVES = ['Sink', 'Hob', 'Oven', 'Dishwasher', 'Fridge freezer', 'Larder', 'Pan drawers', 'Bin pull-out'] as const;

export const WALL_LIMITS: [number, number] = [1200, 8000];
export const HEIGHT_LIMITS: [number, number] = [2000, 3500];
export const WISHES_MAX = 500;

const ID = /^[a-z0-9][a-z0-9-]{0,63}$/;
const isShape = (v: unknown): v is RoomShape => typeof v === 'string' && Object.prototype.hasOwnProperty.call(SHAPES, v);
const inRange = (v: unknown, [lo, hi]: [number, number]): v is number => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;

export function defaultBrief(shape: RoomShape = 'l'): StartBrief {
  return { v: 1, room: 'kitchen', shape, walls: [...SHAPES[shape].lengths], height: 2400, must: ['Sink', 'Hob', 'Oven', 'Dishwasher', 'Fridge freezer'] };
}

/** Problems a shopper must fix before starting; empty when the brief can be sent. */
export function briefIssues(b: StartBrief): string[] {
  const issues: string[] = [];
  SHAPES[b.shape].walls.forEach((name, i) => {
    if (!inRange(b.walls[i], WALL_LIMITS)) issues.push(`Wall ${name} must be ${WALL_LIMITS[0]}–${WALL_LIMITS[1]}mm.`);
  });
  if (!inRange(b.height, HEIGHT_LIMITS)) issues.push(`Ceiling height must be ${HEIGHT_LIMITS[0]}–${HEIGHT_LIMITS[1]}mm.`);
  if ((b.wishes ?? '').length > WISHES_MAX) issues.push(`Wishes can be up to ${WISHES_MAX} characters.`);
  return issues;
}

/** The brief as a compact object: empty optional fields are left out. */
function compact(b: StartBrief): StartBrief {
  const wishes = b.wishes?.trim();
  return {
    v: 1,
    room: 'kitchen',
    shape: b.shape,
    walls: b.walls.slice(0, SHAPES[b.shape].walls.length),
    height: b.height,
    ...(b.range ? { range: b.range } : {}),
    ...(b.colour ? { colour: b.colour } : {}),
    must: b.must.filter((m) => (MUST_HAVES as readonly string[]).includes(m)),
    ...(wishes ? { wishes } : {}),
  };
}

export function encodeStartBrief(b: StartBrief): string {
  return encodeURIComponent(JSON.stringify(compact(b)));
}

/** The planner link for a brief. `plannerHref` is the planner's path, e.g. `/kitchens/planner/`. */
export function startHref(plannerHref: string, b: StartBrief): string {
  return `${plannerHref}${plannerHref.includes('?') ? '&' : '?'}start=${encodeStartBrief(b)}`;
}

/**
 * Reads a `?start=` value (already URI-decoded, as `URLSearchParams.get` gives
 * it). Returns undefined when it is not a brief at all; otherwise a brief with
 * every invalid field replaced by the default or dropped.
 */
export function decodeStartBrief(value: string | null | undefined): StartBrief | undefined {
  if (!value) return undefined;
  let raw: unknown;
  try {
    raw = JSON.parse(value);
  } catch {
    return undefined;
  }
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object' || r.v !== 1) return undefined;
  const shape = isShape(r.shape) ? r.shape : 'l';
  const base = defaultBrief(shape);
  const walls = SHAPES[shape].lengths.map((fallback, i) => {
    const w = Array.isArray(r.walls) ? r.walls[i] : undefined;
    return inRange(w, WALL_LIMITS) ? w : fallback;
  });
  const must = Array.isArray(r.must) ? r.must.filter((m): m is string => typeof m === 'string' && (MUST_HAVES as readonly string[]).includes(m)) : base.must;
  const wishes = typeof r.wishes === 'string' ? r.wishes.trim().slice(0, WISHES_MAX) : '';
  return {
    v: 1,
    room: 'kitchen',
    shape,
    walls,
    height: inRange(r.height, HEIGHT_LIMITS) ? r.height : base.height,
    ...(typeof r.range === 'string' && ID.test(r.range) ? { range: r.range } : {}),
    ...(typeof r.colour === 'string' && ID.test(r.colour) ? { colour: r.colour } : {}),
    must: [...new Set(must)],
    ...(wishes ? { wishes } : {}),
  };
}
